import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SlidersHorizontal, X, ArrowDownUp } from "lucide-react";

const SORTS = [
  { id: "",            label: "Recommended" },
  { id: "price_asc",   label: "Price: low → high" },
  { id: "price_desc",  label: "Price: high → low" },
  { id: "rating",      label: "Top rated" },
  { id: "best_selling", label: "Best selling" },
  { id: "newest",      label: "Newest" },
];

export default function SearchFilters({ value, onChange, categoryId, hideCategoryPicker }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [cats, setCats] = useState([]);
  const [draft, setDraft] = useState(value || {});

  useEffect(() => {
    // Refresh size/gender list whenever the EFFECTIVE category changes —
    // including the live drawer selection, not just the committed one.
    // Without watching `draft.category_id` the picker keeps showing the
    // mixed-bag list from every category until the user clicks Apply.
    const cid = draft.category_id || value?.category_id || categoryId;
    api.get(`/search/meta${cid ? `?category_id=${cid}` : ""}`)
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, [categoryId, value?.category_id, draft.category_id]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => { setDraft(value || {}); }, [value]);

  const apply = () => { onChange(draft); setOpen(false); };
  const clear = () => { const empty = {}; setDraft(empty); onChange(empty); };

  const activeCount = Object.values(value || {}).filter(v => v !== "" && v != null && v !== false).length;

  // compact filter chips for top bar
  return (
    <>
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2">
        <button
          onClick={() => setOpen(true)}
          data-testid="filters-open-btn"
          className={`chip shrink-0 ${activeCount ? "active" : ""}`}
        >
          <SlidersHorizontal size={12}/> Filters{activeCount ? ` · ${activeCount}` : ""}
        </button>

        {/* Sort dropdown as chip */}
        <div className="relative shrink-0">
          <select
            value={value?.sort || ""}
            onChange={e => onChange({ ...value, sort: e.target.value })}
            data-testid="filters-sort-select"
            className="chip pr-8 appearance-none cursor-pointer bg-[#261534]"
          >
            {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <ArrowDownUp size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#A1A1AA]"/>
        </div>

        {/* Quick chips for active values.
            Skip `category_id` when a parent page is already pinning the
            category (it'd render as "category id: cat_x" which is confusing). */}
        {Object.entries(value || {})
          .filter(([k, v]) => v && k !== "sort" && !(hideCategoryPicker && k === "category_id"))
          .map(([k, v]) => (
          <button key={k} onClick={() => onChange({ ...value, [k]: undefined })}
            data-testid={`filter-chip-${k}`}
            className="chip active shrink-0">
            {k.replace(/_/g, " ")}: {String(v)} <X size={12}/>
          </button>
        ))}

        {activeCount > 0 && (
          <button onClick={clear} data-testid="filters-clear-btn" className="chip shrink-0 text-[#FF007F]">Clear all</button>
        )}
      </div>

      {/* Drawer / Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={()=>setOpen(false)}>
          <div className="bg-[#261534] w-full max-w-lg sm:rounded-3xl rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-black uppercase text-2xl">Filters</div>
              <button onClick={()=>setOpen(false)} data-testid="filters-close-btn"><X size={20}/></button>
            </div>

            {/* Category */}
            {!hideCategoryPicker && cats.length > 0 && (
              <Section title="Category">
                <div className="flex gap-2 flex-wrap">
                  <button onClick={()=>setDraft({...draft, category_id: "", sizes: undefined, size: undefined})}
                    data-testid="filter-cat-all"
                    className={`chip ${!draft.category_id ? "active" : ""}`}>All</button>
                  {cats.map(c => (
                    <button key={c.category_id} onClick={()=>setDraft({...draft, category_id: c.category_id, sizes: undefined, size: undefined})}
                      data-testid={`filter-cat-${c.category_id}`}
                      className={`chip ${draft.category_id === c.category_id ? "active" : ""}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* Gender */}
            {meta?.show_gender && (
              <Section title="Gender">
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: "", label: "All" },
                    { id: "men", label: "Men" },
                    { id: "women", label: "Women" },
                    { id: "unisex", label: "Unisex" },
                    { id: "kids", label: "Kids" },
                  ].map(g => (
                    <button key={g.id || "all"} onClick={()=>setDraft({...draft, gender: g.id})}
                      data-testid={`filter-gender-${g.id||"all"}`}
                      className={`chip ${(draft.gender||"")===g.id?"active":""}`}>
                      {g.label}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* Size — multi-select */}
            {meta?.show_size && meta?.sizes?.length > 0 && (
              <Section title="Size (pick one or more)">
                <div className="flex gap-2 flex-wrap">
                  {meta.sizes.map(s => {
                    const arr = Array.isArray(draft.sizes) ? draft.sizes : (draft.size ? [draft.size] : []);
                    const on = arr.includes(s);
                    const toggle = () => {
                      const next = on ? arr.filter(x => x !== s) : [...arr, s];
                      setDraft({ ...draft, sizes: next.length ? next : undefined, size: undefined });
                    };
                    return (
                      <button key={s} onClick={toggle}
                        data-testid={`filter-size-${s}`}
                        className={`chip ${on ? "active" : ""}`}>{s}</button>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Price range — filter values are USD (canonical); label says so explicitly */}
            {meta && (
              <Section title={`Price (USD) · $${draft.min_price ?? meta.min_price} – $${draft.max_price ?? meta.max_price}`}>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder={`Min $${meta.min_price}`} className="input-dark"
                    value={draft.min_price ?? ""}
                    onChange={e=>setDraft({...draft, min_price: e.target.value ? +e.target.value : undefined})}
                    data-testid="filter-min-price"/>
                  <input type="number" placeholder={`Max $${meta.max_price}`} className="input-dark"
                    value={draft.max_price ?? ""}
                    onChange={e=>setDraft({...draft, max_price: e.target.value ? +e.target.value : undefined})}
                    data-testid="filter-max-price"/>
                </div>
              </Section>
            )}

            {/* Rating */}
            <Section title="Min rating">
              <div className="flex gap-2 flex-wrap">
                {[0, 3, 4, 4.5].map(r => (
                  <button key={r} onClick={()=>setDraft({...draft, min_rating: r||undefined})}
                    data-testid={`filter-rating-${r}`}
                    className={`chip ${(draft.min_rating||0)===r?"active":""}`}>
                    {r === 0 ? "Any" : `★ ${r}+`}
                  </button>
                ))}
              </div>
            </Section>

            {/* Delivery */}
            <Section title="Delivery time">
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: undefined, l: "Any" },
                  { v: 2, l: "≤ 2 days" },
                  { v: 5, l: "≤ 5 days" },
                  { v: 7, l: "≤ 7 days" },
                  { v: 14, l: "≤ 14 days" },
                ].map(d => (
                  <button key={String(d.v)} onClick={()=>setDraft({...draft, max_delivery_days: d.v})}
                    data-testid={`filter-delivery-${d.v||"any"}`}
                    className={`chip ${(draft.max_delivery_days || undefined)===d.v?"active":""}`}>
                    {d.l}
                  </button>
                ))}
              </div>
            </Section>

            {/* Pincode */}
            <Section title="Deliver to (pincode)">
              <input type="text" placeholder="e.g. 10001" className="input-dark"
                value={draft.pincode ?? ""}
                onChange={e=>setDraft({...draft, pincode: e.target.value || undefined})}
                data-testid="filter-pincode"/>
              {meta?.sample_pincodes && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {meta.sample_pincodes.slice(0, 4).map(p => (
                    <button key={p} onClick={()=>setDraft({...draft, pincode: p})} className="chip text-xs">
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </Section>

            <div className="flex gap-2 mt-4 sticky bottom-0 bg-[#261534] pt-3 -mx-5 px-5 border-t border-[#3D2845]">
              <button onClick={()=>{setDraft({}); }} className="btn-secondary flex-1" data-testid="filters-reset-btn">Reset</button>
              <button onClick={apply} className="btn-primary flex-1" data-testid="filters-apply-btn">Apply</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="font-display font-bold uppercase text-xs tracking-widest text-[#A1A1AA] mb-2">{title}</div>
      {children}
    </div>
  );
}
